import { SelfServiceError } from '@/Services/BKAPI/Errors/SelfServiceError';
import { Money, RefundQueryResult } from '@/Services/BKAPI/models';
import type { SelfServiceAvailableRefundResponse } from '@bookingkit/api-v4';

type WireMoney = { value?: number; currency?: string };
type WireBalance = { money?: WireMoney };

// The available-refund payload may arrive in either casing: the API currently sends snake_case
// (refundable_amount / paid_amount / explanation_codes) while the SDK types it camelCase. We read
// both so this keeps working before AND after the SDK is corrected (whichever way it lands).
type AvailableRefundPayload = {
    refundable_amount?: WireBalance;
    refundableAmount?: WireBalance;
    paid_amount?: WireMoney;
    paidAmount?: WireMoney;
    explanation_codes?: string[];
    explanationCodes?: string[];
    // Per FEE-2210 the API distinguishes full vs partial refund explicitly.
    full_refund?: boolean;
    fullRefund?: boolean;
};

const toMoney = (money?: WireMoney): Money | undefined => {
    if (typeof money?.value !== 'number' || !money.currency) {
        return undefined;
    }
    return { value: money.value, currency: money.currency };
};

export const fromAPIAvailableRefund = (
    response: SelfServiceAvailableRefundResponse
): RefundQueryResult => {
    const payload = response as unknown as AvailableRefundPayload;

    const refundableMoney = (
        payload.refundable_amount ?? payload.refundableAmount
    )?.money;
    const refundableAmount = toMoney(refundableMoney);
    if (!refundableAmount) {
        throw SelfServiceError.fromMissingProperty(
            'refundable_amount.money',
            'MoneyResponse',
            refundableMoney
        );
    }

    return {
        refundableAmount,
        paidAmount: toMoney(payload.paid_amount ?? payload.paidAmount),
        explanation_codes:
            payload.explanation_codes ?? payload.explanationCodes ?? [],
        fullRefund: payload.full_refund ?? payload.fullRefund,
    };
};
